import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { create } from 'zustand';
import { useAuthStore } from './authStore';

const API = `${RUNTIME_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const useDataStore = create((set, get) => ({
  // Dashboard
  dashboard: null,
  dashboardLoading: false,

  // Customers
  customers: [],
  customersLoading: false,

  // Appointments
  appointments: [],
  appointmentsLoading: false,

  // Shoots
  shoots: [],
  shootsLoading: false,

  // Packages
  packages: [],
  packagesLoading: false,

  // Team
  team: [],
  teamLoading: false,

  // Venues
  venues: [],
  venuesLoading: false,

  // Notifications
  notifications: [],
  notificationsLoading: false,

  // Calendar
  calendarEvents: [],
  calendarLoading: false,

  // Albums
  albums: [],
  albumsLoading: false,
  currentAlbum: null,
  albumPhotos: [],
  albumPhotosLoading: false,

  // Global Upload State
  activeUploads: {}, // { albumId: { customerId, customerName, albumName, current, total, percent, estimatedTimeLeft, cancelled } }
  
  // Upload state yönetimi
  setUploadProgress: (albumId, data) => {
    set(state => ({
      activeUploads: {
        ...state.activeUploads,
        [albumId]: { ...state.activeUploads[albumId], ...data }
      }
    }));
  },
  
  clearUpload: (albumId) => {
    set(state => {
      const newUploads = { ...state.activeUploads };
      delete newUploads[albumId];
      return { activeUploads: newUploads };
    });
  },
  
  cancelUpload: (albumId) => {
    set(state => ({
      activeUploads: {
        ...state.activeUploads,
        [albumId]: { ...state.activeUploads[albumId], cancelled: true }
      }
    }));
  },

  // Dashboard
  fetchDashboard: async () => {
    set({ dashboardLoading: true });
    try {
      const response = await fetch(`${API}/dashboard`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ dashboard: data, dashboardLoading: false });
      }
    } catch (error) {
      console.error('Dashboard fetch failed:', error);
      set({ dashboardLoading: false });
    }
  },

  // Customers
  fetchCustomers: async () => {
    set({ customersLoading: true });
    try {
      const response = await fetch(`${API}/customers`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ customers: data, customersLoading: false });
      }
    } catch (error) {
      console.error('Customers fetch failed:', error);
      set({ customersLoading: false });
    }
  },

  addCustomer: async (customer) => {
    const response = await fetch(`${API}/customers`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(customer)
    });
    if (!response.ok) throw new Error('Müşteri eklenemedi');
    const data = await response.json();
    set(state => ({ customers: [...state.customers, data] }));
    return data;
  },

  updateCustomer: async (id, customer) => {
    const response = await fetch(`${API}/customers/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(customer)
    });
    if (!response.ok) throw new Error('Müşteri güncellenemedi');
    const data = await response.json();
    set(state => ({
      customers: state.customers.map(c => c.id === id ? data : c)
    }));
    return data;
  },

  deleteCustomer: async (id) => {
    const response = await fetch(`${API}/customers/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Müşteri silinemedi');
    set(state => ({
      customers: state.customers.filter(c => c.id !== id)
    }));
  },

  // Appointments
  fetchAppointments: async () => {
    set({ appointmentsLoading: true });
    try {
      const response = await fetch(`${API}/appointments`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ appointments: data, appointmentsLoading: false });
      }
    } catch (error) {
      console.error('Appointments fetch failed:', error);
      set({ appointmentsLoading: false });
    }
  },

  addAppointment: async (appointment) => {
    const response = await fetch(`${API}/appointments`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(appointment)
    });
    if (!response.ok) throw new Error('Randevu eklenemedi');
    const data = await response.json();
    set(state => ({ appointments: [...state.appointments, data] }));
    return data;
  },

  updateAppointmentStatus: async (id, status) => {
    const response = await fetch(`${API}/appointments/${id}/status?status=${status}`, {
      method: 'PUT',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Durum güncellenemedi');
    set(state => ({
      appointments: state.appointments.map(a => a.id === id ? { ...a, status } : a)
    }));
  },

  deleteAppointment: async (id) => {
    const response = await fetch(`${API}/appointments/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Randevu silinemedi');
    set(state => ({
      appointments: state.appointments.filter(a => a.id !== id)
    }));
  },

  // Shoots
  fetchShoots: async () => {
    set({ shootsLoading: true });
    try {
      const response = await fetch(`${API}/shoots`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ shoots: data, shootsLoading: false });
      }
    } catch (error) {
      console.error('Shoots fetch failed:', error);
      set({ shootsLoading: false });
    }
  },

  addShoot: async (shoot) => {
    const response = await fetch(`${API}/shoots`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(shoot)
    });
    if (!response.ok) throw new Error('Çekim eklenemedi');
    const data = await response.json();
    set(state => ({ shoots: [...state.shoots, data] }));
    return data;
  },

  updateShootStatus: async (id, status) => {
    const response = await fetch(`${API}/shoots/${id}/status?status=${status}`, {
      method: 'PUT',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Durum güncellenemedi');
    set(state => ({
      shoots: state.shoots.map(s => s.id === id ? { ...s, status } : s)
    }));
  },

  deleteShoot: async (id) => {
    const response = await fetch(`${API}/shoots/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Çekim silinemedi');
    set(state => ({
      shoots: state.shoots.filter(s => s.id !== id)
    }));
  },

  // Packages
  fetchPackages: async () => {
    set({ packagesLoading: true });
    try {
      const response = await fetch(`${API}/packages`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ packages: data, packagesLoading: false });
      }
    } catch (error) {
      console.error('Packages fetch failed:', error);
      set({ packagesLoading: false });
    }
  },

  addPackage: async (pkg) => {
    const response = await fetch(`${API}/packages`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(pkg)
    });
    if (!response.ok) throw new Error('Paket eklenemedi');
    const data = await response.json();
    set(state => ({ packages: [...state.packages, data] }));
    return data;
  },

  deletePackage: async (id) => {
    const response = await fetch(`${API}/packages/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Paket silinemedi');
    set(state => ({
      packages: state.packages.filter(p => p.id !== id)
    }));
  },

  // Team
  fetchTeam: async () => {
    set({ teamLoading: true });
    try {
      const response = await fetch(`${API}/team`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ team: data, teamLoading: false });
      }
    } catch (error) {
      console.error('Team fetch failed:', error);
      set({ teamLoading: false });
    }
  },

  addTeamMember: async (member) => {
    const response = await fetch(`${API}/team`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(member)
    });
    if (!response.ok) throw new Error('Ekip üyesi eklenemedi');
    const data = await response.json();
    set(state => ({ team: [...state.team, data] }));
    return data;
  },

  deleteTeamMember: async (id) => {
    const response = await fetch(`${API}/team/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Ekip üyesi silinemedi');
    set(state => ({
      team: state.team.filter(t => t.id !== id)
    }));
  },

  // Venues
  fetchVenues: async () => {
    set({ venuesLoading: true });
    try {
      const response = await fetch(`${API}/venues`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ venues: data, venuesLoading: false });
      }
    } catch (error) {
      console.error('Venues fetch failed:', error);
      set({ venuesLoading: false });
    }
  },

  addVenue: async (venue) => {
    const response = await fetch(`${API}/venues`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(venue)
    });
    if (!response.ok) throw new Error('Mekan eklenemedi');
    const data = await response.json();
    set(state => ({ venues: [...state.venues, data] }));
    return data;
  },

  deleteVenue: async (id) => {
    const response = await fetch(`${API}/venues/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Mekan silinemedi');
    set(state => ({
      venues: state.venues.filter(v => v.id !== id)
    }));
  },

  // Notifications
  fetchNotifications: async () => {
    set({ notificationsLoading: true });
    try {
      const response = await fetch(`${API}/notifications`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ notifications: data, notificationsLoading: false });
      }
    } catch (error) {
      console.error('Notifications fetch failed:', error);
      set({ notificationsLoading: false });
    }
  },

  sendNotification: async (customerId, message, channel) => {
    const response = await fetch(`${API}/notifications`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ customer_id: customerId, message, channel })
    });
    if (!response.ok) throw new Error('Bildirim gönderilemedi');
    const data = await response.json();
    set(state => ({ notifications: [data, ...state.notifications] }));
    return data;
  },

  // Calendar
  fetchCalendar: async (month, year) => {
    set({ calendarLoading: true });
    try {
      const response = await fetch(`${API}/calendar?month=${month}&year=${year}`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ calendarEvents: data.events, calendarLoading: false });
      }
    } catch (error) {
      console.error('Calendar fetch failed:', error);
      set({ calendarLoading: false });
    }
  },

  // Albums
  fetchAlbums: async () => {
    set({ albumsLoading: true });
    try {
      const response = await fetch(`${API}/albums`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ albums: data, albumsLoading: false });
      }
    } catch (error) {
      console.error('Albums fetch failed:', error);
      set({ albumsLoading: false });
    }
  },

  createAlbum: async (customerId, name, description) => {
    const response = await fetch(`${API}/albums`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ customer_id: customerId, name, description })
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Albüm oluşturulamadı');
    }
    const data = await response.json();
    set(state => ({ albums: [data, ...state.albums] }));
    return data;
  },

  fetchAlbumByCustomer: async (customerId) => {
    try {
      const response = await fetch(`${API}/albums/customer/${customerId}`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ currentAlbum: data });
        return data;
      }
      return null;
    } catch (error) {
      console.error('Album fetch failed:', error);
      return null;
    }
  },

  fetchAlbumPhotos: async (albumId) => {
    set({ albumPhotosLoading: true });
    try {
      const response = await fetch(`${API}/albums/${albumId}/photos`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        set({ albumPhotos: data, albumPhotosLoading: false });
        return data;
      }
    } catch (error) {
      console.error('Photos fetch failed:', error);
      set({ albumPhotosLoading: false });
    }
    return [];
  },

  uploadPhotos: async (albumId, files, subfolder = '') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (subfolder) formData.append('subfolder', subfolder);

    const token = useAuthStore.getState().token;
    const response = await fetch(`${API}/albums/${albumId}/photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (!response.ok) throw new Error('Fotoğraflar yüklenemedi');
    return await response.json();
  },

  // Alt-klasör API'leri
  listSubfolders: async (albumId) => {
    const response = await fetch(`${API}/albums/${albumId}/subfolders`, { headers: getHeaders() });
    if (!response.ok) throw new Error('Alt-klasörler alınamadı');
    return await response.json();
  },
  renameSubfolder: async (albumId, oldName, newName) => {
    const response = await fetch(`${API}/albums/${albumId}/subfolders/rename`, {
      method: 'PUT', headers: getHeaders(), body: JSON.stringify({ old_name: oldName, new_name: newName })
    });
    if (!response.ok) throw new Error('Alt-klasör yeniden adlandırılamadı');
    return await response.json();
  },
  movePhotosToSubfolder: async (albumId, photoIds, subfolder = '') => {
    const response = await fetch(`${API}/albums/${albumId}/photos/move`, {
      method: 'PUT', headers: getHeaders(), body: JSON.stringify({ photo_ids: photoIds, subfolder })
    });
    if (!response.ok) throw new Error('Taşıma başarısız');
    return await response.json();
  },

  deletePhoto: async (albumId, photoId) => {
    const response = await fetch(`${API}/albums/${albumId}/photos/${photoId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Fotoğraf silinemedi');
    set(state => ({
      albumPhotos: state.albumPhotos.filter(p => p.id !== photoId)
    }));
  },

  deleteAlbum: async (albumId) => {
    const response = await fetch(`${API}/albums/${albumId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Albüm silinemedi');
    set(state => ({
      albums: state.albums.filter(a => a.id !== albumId),
      currentAlbum: null,
      albumPhotos: []
    }));
  },

  downloadSelectedPhotos: async (albumId) => {
    const token = useAuthStore.getState().token;
    const response = await fetch(`${API}/albums/${albumId}/download-selected`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'İndirme başarısız');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'secilen_fotograflar.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }
}));
